import React, { useState, useEffect } from "react";
import { Bar, Line, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import { UserIcon, ChartBarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { Loader } from '../Loader/Loader';
import QuotationDashboard from './QuotationDashboard'
import NotificationsDashboard from "./NotificationsDashboard";
import { decryptData } from "../../utils/storageCrypto";

ChartJS.register(...registerables);

const Home = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterRole, setFilterRole] = useState('');
  const navigate = useNavigate();

  const getUserActivity = process.env.REACT_APP_FORE_APILINK + `/user-activity/get-user-activity?hubId=${sessionStorage.getItem('hubId')}`;

  useEffect(() => {
    const fetchUserActivities = async () => {
      const encryptedToken = sessionStorage.getItem('token');
      const token = decryptData(encryptedToken);
      try {
        const response = await fetch(getUserActivity, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-token': token,
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log(data);
        setActivities(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
      finally {
        setLoading(false);
      }
    };

    fetchUserActivities();
  }, []);

if (loading) {
  return <Loader />;
} 

 if (error) return <div className="flex items-center justify-center h-full"><div>Error: {error}</div></div>;

  const formatDateTime = (isoDate) => {
    const dateObj = new Date(isoDate);
    const date = dateObj.toLocaleDateString();
    const time = dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    });
    return `${date} ${time}`;
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedActivities = activities
    .filter(activity => 
      activity.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.activity.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(activity => filterRole ? activity.role === filterRole : true)
    .sort((a, b) => {
      if (a[sortField] < b[sortField]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortField] > b[sortField]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAndSortedActivities.slice(indexOfFirstItem, indexOfLastItem);

  const processActivityText = (text) => {
    const parts = text.split(/"([^"]*)"/);
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        const [label, idInfo] = part.split(/:\((.+)\)/);
        return (
          <span
            key={index}
            className="text-blue-500 cursor-pointer hover:underline"
            onClick={() => handleClickedWord(idInfo, part)}
          >
            {label}
          </span>
        );
      }
      return part;
    });
  };
  
  const handleClickedWord = (idInfo, fullPart) => {
    const [idType, id] = idInfo.split(': ');
    switch (idType.trim()) {
      case 'projectId':
        navigate(`/admin/app/projects/projectdetails`, { state: { projectData:{ _id: id.trim() }} });
        break;
      case 'customerId':
        navigate(`/admin/app/sales/customerdetails`, { state: { customerId: id.trim() } });
        break;
      case 'quotationId':
        navigate(`/admin/app/sales/quotedetails`, { state: { quotationId: id.trim() } });
        break;
      case 'salesorderId':
        navigate(`/admin/app/sales/salesorderdetails`, { state: { salesOrderId: id.trim() } });
        break;
      default:
        console.log("Unknown ID type:", idType, "Full part:", fullPart);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div 
          className="min-h-full bg-contain bg-center"
          style={{ backgroundImage: "url('/screenBacground.svg')" }}
        >
          <div className="space-y-6 p-6">
            {/* User Activity Section */}
            {currentItems.length > 0 ? (
              (sessionStorage.getItem('roleId') === '1' || sessionStorage.getItem('roleId') === '0') && (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="relative">
                    <div className="absolute inset-0 bg-[#F9F9FB] rounded-t-lg"></div>
                    <div className="relative flex justify-between items-center p-4 border-b">
                      <h2 className="text-lg font-semibold text-gray-800">User Activity</h2>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">
                          Page {currentPage} of {Math.ceil(filteredAndSortedActivities.length / itemsPerPage)}
                        </span>
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1 text-sm text-blue-500 disabled:text-gray-300 hover:bg-blue-50 rounded transition-colors"
                        >
                          &lt;
                        </button>
                        <button
                          onClick={() => setCurrentPage(prev => prev + 1)}
                          disabled={filteredAndSortedActivities.length <= itemsPerPage || currentPage === Math.ceil(filteredAndSortedActivities.length / itemsPerPage)}
                          className="px-3 py-1 text-sm text-blue-500 disabled:text-gray-300 hover:bg-blue-50 rounded transition-colors"
                        >
                          &gt;
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-2 sm:space-y-0 sm:space-x-4">
                      <input
                        type="text"
                        placeholder="Search activities..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full sm:w-64 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <select
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                        className="w-full sm:w-48 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">All Roles</option>
                        <option value="Super Admin">Super Admin</option>
                        <option value="Admin">Admin</option>
                        <option value="Marketing Associate">Marketing Associate</option>
                        <option value="Field Associate">Field Associate</option>
                        <option value="Customer">Customer</option>
                      </select>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full text-center border-collapse">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('createdAt')}>
                              Date {sortField === 'createdAt' && (sortDirection === 'asc' ? '▲' : '▼')}
                            </th>
                            <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('username')}>
                              User Name {sortField === 'username' && (sortDirection === 'asc' ? '▲' : '▼')}
                            </th>
                            <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('role')}>
                              Role {sortField === 'role' && (sortDirection === 'asc' ? '▲' : '▼')}
                            </th>
                            <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('activity')}>
                              Activity {sortField === 'activity' && (sortDirection === 'asc' ? '▲' : '▼')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((activity, index) => (
                            <tr key={activity.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                              <td className="p-3 text-sm border">{formatDateTime(activity.createdAt)}</td>
                              <td className="p-3 text-sm border font-medium">{activity.username}</td>
                              <td className="p-3 text-sm border">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                  {activity.role}
                                </span>
                              </td>
                              <td className="p-3 text-sm border text-left">{processActivityText(activity.activity, activity.data)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8">
                <div className="text-center text-gray-500">
                  <div className="mb-4">
                    <UserIcon className="mx-auto h-12 w-12 text-gray-300" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">No User Activities</h3>
                  <p className="text-sm">User activities will appear here when available.</p>
                </div>
              </div>
            )}

            {/* Notifications Dashboard Section */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <NotificationsDashboard />
            </div>

            {/* Quotations Dashboard Section */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <QuotationDashboard />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
